import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { Recommendation } from '../schemas';
import type { ResultsCarouselSection } from '../lib/resultsAdapters';

/**
 * Recipient-keyed registry of in-flight board regenerates.
 *
 * This is the page-local `refreshSnapshot` (RecommendationResultsPage, bug #43)
 * lifted OUT of the page so it survives navigation. Two payoffs
 * (BOARD_REGENERATE_LOADING_SPEC.md §5–6):
 *
 *  1. Scope — the progress banner is gated on `entries[currentRecipientId]`,
 *     so a Mom regenerate can NEVER render on Dad's board / Calendar / Events.
 *  2. Return-mid-flight — if the user wanders to another board and back while
 *     the ~10–30s agent is still running, the page reads the registry and
 *     restores the previous picks under the centered progress scrim,
 *     instead of the anonymous full-page skeleton it shows today.
 *
 * State only; the easing/capping of the percent lives in the hook that reads
 * the carousel session, and the actual regenerate call stays in the page.
 * Held in memory (a hard browser reload clears it — acceptable per spec; the
 * new picks are still saved server-side and appear on the next load).
 */
export interface RegenerateEntry {
  recipientId: string;
  /** The picks shown at regenerate-start — kept on screen during the wait. */
  snapshotDoc: Recommendation;
  snapshotSections: ResultsCarouselSection[];
  /** The new recommendation id, once the submit callable returns it. */
  expectedRecommendationId: string | null;
  /** Wall-clock ms at start (passed in by the caller — keeps this pure). */
  startedAt: number;
}

export interface RegenerateProgressValue {
  entries: Record<string, RegenerateEntry>;
  /** Record a regenerate that just kicked off for a recipient. */
  start: (
    entry: Omit<RegenerateEntry, 'expectedRecommendationId'> & {
      expectedRecommendationId?: string | null;
    },
  ) => void;
  /** Attach the new recommendation id once the submit callable resolves. */
  setExpected: (recipientId: string, recommendationId: string) => void;
  /** Drop the entry — on swap-to-new, failure, or the safety-net timeout. */
  clear: (recipientId: string) => void;
}

const RegenerateProgressContext = createContext<RegenerateProgressValue | null>(null);

export const RegenerateProgressProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [entries, setEntries] = useState<Record<string, RegenerateEntry>>({});

  const start = useCallback<RegenerateProgressValue['start']>((entry) => {
    setEntries((prev) => ({
      ...prev,
      [entry.recipientId]: {
        expectedRecommendationId: null,
        ...entry,
      },
    }));
  }, []);

  const setExpected = useCallback<RegenerateProgressValue['setExpected']>(
    (recipientId, recommendationId) => {
      setEntries((prev) => {
        const cur = prev[recipientId];
        if (!cur) return prev;
        return {
          ...prev,
          [recipientId]: { ...cur, expectedRecommendationId: recommendationId },
        };
      });
    },
    [],
  );

  const clear = useCallback<RegenerateProgressValue['clear']>((recipientId) => {
    setEntries((prev) => {
      if (!(recipientId in prev)) return prev;
      const next = { ...prev };
      delete next[recipientId];
      return next;
    });
  }, []);

  const value = useMemo<RegenerateProgressValue>(
    () => ({ entries, start, setExpected, clear }),
    [entries, start, setExpected, clear],
  );

  return (
    <RegenerateProgressContext.Provider value={value}>
      {children}
    </RegenerateProgressContext.Provider>
  );
};

/** Full registry API. Throws if no provider is mounted above. */
export function useRegenerateProgress(): RegenerateProgressValue {
  const ctx = useContext(RegenerateProgressContext);
  if (!ctx) {
    throw new Error('useRegenerateProgress must be used within RegenerateProgressProvider');
  }
  return ctx;
}

/**
 * Convenience selector — the in-flight entry for one recipient (or undefined).
 * Re-renders when that recipient's entry changes. Safe to call with an
 * undefined id (returns undefined) so callers don't need to guard.
 */
export function useRegenerateEntry(
  recipientId: string | undefined,
): RegenerateEntry | undefined {
  const { entries } = useRegenerateProgress();
  return recipientId ? entries[recipientId] : undefined;
}
